<script lang="ts">
  // Tela de CAMPEAO — aparece quando o jogador vence o torneio inteiro.
  // Leonardo 2026-05-31: "qnd ganha o torneio poe uma telinha dahora".
  // Trofeu gigante + confete + nome do torneio + nova pontuacao.
  import type { Tournament } from '../../stores/tournaments';
  import { championCelebration } from '../../stores/tournaments';
  import { userId, userScore } from '../../stores/auth';
  import { matchSnapshot } from '../../stores/match';

  type Props = { tournament: Tournament };
  let { tournament: t }: Props = $props();

  // Dupla campea (2v2) ou solo (1v1) — pega do time vencedor da final.
  let champTeam = $derived.by<string[]>(() => {
    const finalRound = t.bracket?.rounds?.[t.bracket.rounds.length - 1];
    const finalMatch = finalRound?.matches?.[0];
    if (!finalMatch?.winner) return [];
    return finalMatch.winner === 'A' ? finalMatch.team_a : finalMatch.team_b;
  });
  function pidName(pid: string): string {
    if (pid === $userId) return 'VOCÊ';
    return t.player_names?.[pid] || t.bot_names?.[pid] || (pid.startsWith('bot-') ? 'bot' : pid.slice(0, 8));
  }
  let partner = $derived(champTeam.find((p) => p !== $userId));

  // 24 confetes com posicao/cor/delay deterministicos por index.
  const COLORS = ['#ffd54f', '#42a5f5', '#66bb6a', '#ef5350', '#ab47bc', '#ff8f00'];
  const confetti = Array.from({ length: 28 }, (_, i) => ({
    left: (i * 37) % 100,
    color: COLORS[i % COLORS.length],
    delay: (i % 10) * 0.18,
    dur: 2.2 + (i % 5) * 0.35,
    rot: (i * 53) % 360,
  }));

  function close() {
    championCelebration.set(null);
    // Limpa o snapshot da partida final tambem, pra o FinishedModal de
    // baixo (VITORIA) nao reaparecer depois do champion.
    matchSnapshot.set(null);
  }
</script>

<div class="overlay" role="dialog" aria-modal="true">
  <div class="confetti-layer" aria-hidden="true">
    {#each confetti as c}
      <span
        class="confetti"
        style="left:{c.left}%; background:{c.color}; animation-delay:{c.delay}s; animation-duration:{c.dur}s; --rot:{c.rot}deg;"
      ></span>
    {/each}
  </div>

  <div class="card">
    <div class="rays"></div>
    <div class="trophy">🏆</div>
    <p class="eyebrow">{t.name || 'Torneio'}</p>
    <h1 class="title">{partner ? 'DUPLA CAMPEÃ' : 'CAMPEÃO'}</h1>

    <div class="champs">
      {#each champTeam as pid}
        <span class="champ-pill" class:me={pid === $userId}>{pidName(pid)}</span>
      {/each}
    </div>

    <div class="score-badge">
      <span class="trophy-mini">🏆</span>
      <strong>{$userScore}</strong>
      <span class="lbl">RANKING</span>
    </div>

    <button class="close-btn" onclick={close}>VOLTAR AO LOBBY</button>
  </div>
</div>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 1300;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: radial-gradient(circle at 50% 35%, rgba(21, 101, 192, 0.85), rgba(2, 3, 26, 0.95));
    backdrop-filter: blur(8px);
    overflow: hidden;
    animation: fade 0.3s ease-out;
  }
  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }

  .confetti-layer { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
  .confetti {
    position: absolute;
    top: -20px;
    width: 9px;
    height: 14px;
    border-radius: 2px;
    opacity: 0.9;
    animation-name: fall;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  }
  @keyframes fall {
    0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(105vh) rotate(var(--rot)); opacity: 0.9; }
  }

  .card {
    position: relative;
    width: 100%;
    max-width: 360px;
    padding: 28px 24px 22px;
    background: linear-gradient(180deg, #faf5ec 0%, #fff 60%, #f0e7d5 100%);
    border: 4px solid #ffb300;
    outline: 3px solid #0D47A1;
    outline-offset: -3px;
    border-radius: 18px;
    box-shadow: 0 8px 0 #b37400, 0 22px 60px rgba(0, 0, 0, 0.55);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    animation: pop 0.5s cubic-bezier(0.2, 1.5, 0.4, 1);
    overflow: hidden;
  }
  @keyframes pop {
    0% { transform: scale(0.6) translateY(20px); opacity: 0; }
    60% { transform: scale(1.06); }
    100% { transform: scale(1); opacity: 1; }
  }
  .rays {
    position: absolute;
    top: -40%;
    left: -40%;
    width: 180%;
    height: 180%;
    background: repeating-conic-gradient(rgba(255, 210, 74, 0.18) 0deg 12deg, transparent 12deg 24deg);
    animation: spin 14s linear infinite;
    pointer-events: none;
    z-index: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .trophy {
    position: relative;
    z-index: 1;
    font-size: 72px;
    line-height: 1;
    filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.3));
    animation: bob 2.4s ease-in-out infinite;
  }
  @keyframes bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }

  .eyebrow {
    position: relative;
    z-index: 1;
    margin: 0;
    font-size: 12px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #1976D2;
    font-weight: 800;
  }
  .title {
    position: relative;
    z-index: 1;
    margin: 0;
    font-size: 30px;
    font-weight: 900;
    letter-spacing: 2px;
    color: #e65100;
    text-shadow: 0 2px 0 #ffd54f, 0 3px 4px rgba(0, 0, 0, 0.2);
  }
  .champs {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
    margin: 4px 0;
  }
  .champ-pill {
    padding: 7px 16px;
    border-radius: 999px;
    font-weight: 900;
    font-size: 14px;
    background: rgba(21, 101, 192, 0.1);
    border: 2px solid #1565C0;
    color: #0D47A1;
  }
  .champ-pill.me {
    background: linear-gradient(180deg, #ffd54f, #ffb300);
    border-color: #e65100;
    color: #5d2c00;
  }
  .score-badge {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 8px 18px;
    border-radius: 999px;
    background: rgba(13, 71, 161, 0.1);
    border: 2px solid rgba(13, 71, 161, 0.3);
  }
  .trophy-mini { font-size: 14px; }
  .score-badge strong { font-size: 22px; color: #0D47A1; font-weight: 900; }
  .score-badge .lbl { font-size: 9px; letter-spacing: 1.5px; color: #1976D2; }

  .close-btn {
    position: relative;
    z-index: 1;
    margin-top: 6px;
    width: 100%;
    padding: 13px;
    border: 2px solid #0D47A1;
    border-radius: 10px;
    background: linear-gradient(180deg, #42a5f5, #1976d2 55%, #1565c0);
    color: #fff;
    font-weight: 900;
    font-size: 14px;
    letter-spacing: 1.5px;
    cursor: pointer;
    box-shadow: 0 3px 0 #0d3a82, inset 0 1px 0 rgba(255, 255, 255, 0.25);
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
  }
  .close-btn:active { transform: translateY(2px); box-shadow: 0 1px 0 #0d3a82; }
</style>
